import React, { createContext, useCallback, useContext, useMemo } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth } from "./useAuth";
import {
    useSwitchToClientContextMutation,
    useReturnToAgencyContextMutation,
    IAgencyContextResponse,
} from "../services/nestjs-base-api";
import nestjsBaseApi from "../services/nestjs-base-api";

interface AgencyContextType {
    isInAgencyContext: boolean;
    isAgencyUser: boolean;
    agencyContext: IAgencyContextResponse | null;
    clientOrganisationName: string | null;
    switchToClient: (clientOrgId: number) => Promise<void>;
    returnToAgency: () => Promise<void>;
    isLoading: boolean;
}

const AgencyContext = createContext<AgencyContextType>({
    isInAgencyContext: false,
    isAgencyUser: false,
    agencyContext: null,
    clientOrganisationName: null,
    switchToClient: async () => {},
    returnToAgency: async () => {},
    isLoading: false,
});

export const AgencyContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [switchToClientMutation, { isLoading: isSwitching }] = useSwitchToClientContextMutation();
    const [returnToAgencyMutation, { isLoading: isReturning }] = useReturnToAgencyContextMutation();

    const isLoading = isSwitching || isReturning;

    const isAgencyUser = useMemo(() => {
        if (!user?.roles) return false;
        const roles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        return roles.includes('AGENCY_ADMIN') || roles.includes('AGENCY_USER');
    }, [user]);

    const isInAgencyContext = useMemo(() => {
        return !!(user?.agencyContext);
    }, [user]);

    const clientOrganisationName = useMemo(() => {
        return user?.agencyContext?.clientOrganisationName || null;
    }, [user]);

    const agencyContext = useMemo(() => {
        if (!user?.agencyContext) return null;
        return {
            access_token: user.jwtToken,
            agencyContext: user.agencyContext,
            client_organisation: {
                id: user.agencyContext.clientOrganisationId,
                name: user.agencyContext.clientOrganisationName,
                hash: '',
            },
        } as IAgencyContextResponse;
    }, [user]);

    const switchToClient = useCallback(async (clientOrgId: number) => {
        if (!user?.jwtToken) {
            message.error('Authentication required');
            return;
        }

        try {
            const response = await switchToClientMutation({
                jwtToken: user.jwtToken,
                clientOrgId,
            }).unwrap();

            // Build user object with agency context
            const contextUser = {
                ...user,
                jwtToken: response.access_token,
                agencyContext: response.agencyContext,
                // Store original agency user data for return
                originalAgencyUser: {
                    id: user.id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    roles: user.roles,
                    organisation: user.organisation,
                },
                // Override organisation to the client org for dashboard rendering
                organisation: {
                    id: response.client_organisation.id,
                    name: response.client_organisation.name,
                    hash: response.client_organisation.hash,
                    templateType: response.client_organisation.templateType,
                    status: response.client_organisation.status,
                    plan: response.client_organisation.plan,
                },
            };

            // Update localStorage token
            localStorage.setItem('token', response.access_token);

            // Reset RTK Query cache to clear agency data
            dispatch(nestjsBaseApi.util.resetApiState());

            // Update auth context
            login(contextUser);

            message.success(
                `Now viewing: ${response.client_organisation.name}`,
                3
            );

            // Navigate to the client's dashboard
            navigate('/dashboard');
        } catch (error: any) {
            console.error('Failed to switch to client context:', error);
            message.error(error?.data?.message || 'Failed to switch to client view');
        }
    }, [user, switchToClientMutation, login, dispatch, navigate]);

    const returnToAgency = useCallback(async () => {
        if (!user?.jwtToken) {
            message.error('Authentication required');
            return;
        }

        try {
            const response = await returnToAgencyMutation({
                jwtToken: user.jwtToken,
            }).unwrap();

            // Restore original agency user
            const agencyUser = {
                ...user.originalAgencyUser,
                jwtToken: response.access_token,
                organisation: user.originalAgencyUser?.organisation,
            };

            // Remove agency context fields
            delete (agencyUser as any).agencyContext;
            delete (agencyUser as any).originalAgencyUser;

            // Update localStorage token
            localStorage.setItem('token', response.access_token);

            // Reset RTK Query cache
            dispatch(nestjsBaseApi.util.resetApiState());

            // Update auth context
            login(agencyUser);

            message.success('Returned to agency dashboard');

            // Navigate to agency dashboard
            navigate('/agency');
        } catch (error: any) {
            console.error('Failed to return to agency:', error);
            message.error(error?.data?.message || 'Failed to return to agency');
        }
    }, [user, returnToAgencyMutation, login, dispatch, navigate]);

    const value = useMemo(() => ({
        isInAgencyContext,
        isAgencyUser,
        agencyContext,
        clientOrganisationName,
        switchToClient,
        returnToAgency,
        isLoading,
    }), [
        isInAgencyContext,
        isAgencyUser,
        agencyContext,
        clientOrganisationName,
        switchToClient,
        returnToAgency,
        isLoading,
    ]);

    return (
        <AgencyContext.Provider value={value}>
            {children}
        </AgencyContext.Provider>
    );
};

export const useAgencyContext = () => {
    const context = useContext(AgencyContext);
    if (!context) {
        throw new Error('useAgencyContext must be used within an AgencyContextProvider');
    }
    return context;
};
